import * as React from 'react';
import { Select, Textarea, UIText3 } from '@cian/ui-kit';
import { ChangeEvent, FormEvent, ForwardedRef, forwardRef, useMemo } from 'react';
import { EStatus, IOffer } from 'shared/types/listing';
import {
  formatArea,
  formatDate,
  formatLocation,
  formatNumber,
  formatObjectType,
  formatPhone,
  formatPriceType,
  formatReadyAccommodate,
  formatRoomsCount,
  formatStatus,
  formatString,
  formatStringArray,
  getAreaLabel,
} from 'shared/utils/formatters';

import { ContentBlock, ContentRow } from './components';

export interface IDobroListingPopupContentProps {
  offer: IOffer;
  isMobile: boolean;
  formStatus: EStatus;
  onFormStatusChange: (status: EStatus) => void;
  formComment: string;
  onFormCommentChange: (_: ChangeEvent, comment: string) => void;
  onSubmit: (e: FormEvent) => void;
}

export const DobroListingPopupContent = forwardRef(function DobroListingPopupContent(
  props: IDobroListingPopupContentProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { isMobile, offer, formStatus, onFormStatusChange, formComment, onFormCommentChange, onSubmit } = props;

  const statusOptions = useMemo(() => {
    return Object.values(EStatus).map(value => ({
      label: formatStatus(value),
      value,
    }));
  }, []);

  return (
    <div>
      <div ref={ref} data-testid="DobroListingPopupContent">
        <ContentBlock vertical={false}>
          <ContentRow vertical label="Статус">
            {formatStatus(offer.status)}
          </ContentRow>

          <ContentRow vertical label="Дата создания">
            {formatDate(offer.createdAt)}
          </ContentRow>

          {offer.fundName && (
            <ContentRow vertical label="Название фонда">
              {formatString(offer.fundName)}
            </ContentRow>
          )}
        </ContentBlock>

        <ContentBlock>
          <ContentRow label="Тип жилья">{formatObjectType(offer.objectType)}</ContentRow>
          <ContentRow label="Город">{formatLocation(offer.location)}</ContentRow>
          <ContentRow label="Адрес">{formatString(offer.address)}</ContentRow>
          <ContentRow label="Этаж">{formatNumber(offer.floor)}</ContentRow>
        </ContentBlock>

        <ContentBlock>
          <ContentRow label="Кол-во комнат">{formatRoomsCount(offer.roomsCount)}</ContentRow>
          <ContentRow label={getAreaLabel(offer.objectType)}>{formatArea(offer.area)}</ContentRow>
          <ContentRow label="Кол-во гостей">{formatNumber(offer.guestsCount)}</ContentRow>
          <ContentRow label="Кол-во спальных мест">{formatNumber(offer.bedsCount)}</ContentRow>
          <ContentRow label="Спальные места">{formatNumber(offer.bedsCount)}</ContentRow>
          <ContentRow label="В жилье есть">{formatStringArray(offer.facilities)}</ContentRow>
        </ContentBlock>

        <ContentBlock>
          <ContentRow label="На сколько готовы принять">{formatString(offer.availablePeriod)}</ContentRow>
          <ContentRow label="На какой основе">{formatPriceType(offer.priceType)}</ContentRow>
          <ContentRow label="Сумма или комментарий">{formatString(offer.priceDescription)}</ContentRow>
          <ContentRow label="Могу принять">{formatStringArray(offer.availableFor)}</ContentRow>
          <ContentRow label="Готов принять">
            {formatStringArray(offer.readyAccommodate.map(formatReadyAccommodate))}
          </ContentRow>
          <ContentRow label="Есть пожелания">{offer.otherWishes || '—'}</ContentRow>
        </ContentBlock>

        <ContentBlock>
          <ContentRow label="Имя и фамилия">{formatString(offer.name)}</ContentRow>
          <ContentRow label="Телефон">{offer.phone ? formatPhone(offer.phone) : 'Информация скрыта'}</ContentRow>
          <ContentRow label="Дополнительный телефон">
            {offer.additionalPhone ? formatPhone(offer.additionalPhone) : 'Информация скрыта'}
          </ContentRow>
          <ContentRow label="Ник в Telegram">{formatString(offer.telegram || '')}</ContentRow>
        </ContentBlock>
      </div>

      <form onSubmit={onSubmit} data-testid="DobroListingPopupContent-form">
        <ContentBlock border={false} vertical={isMobile}>
          <ContentRow
            vertical
            boldLabel={false}
            fullWidth={isMobile}
            label="Поменять статус на"
            dataTestid="DobroListingPopupContent-form-status"
          >
            <Select
              size="XS"
              value={formStatus}
              options={statusOptions}
              onChange={onFormStatusChange}
              width={isMobile ? '100%' : 186}
            />

            {formStatus === EStatus.Canceled && (
              <UIText3 color="text-negative-default">При переводе в этот статус вы откажетесь от заявки</UIText3>
            )}
          </ContentRow>

          <ContentRow
            vertical
            fullWidth
            boldLabel={false}
            label="Комментарий куратора"
            dataTestid="DobroListingPopupContent-form-comment"
          >
            <Textarea
              minRows={4}
              value={formComment}
              onChange={onFormCommentChange}
              width={isMobile ? '100%' : 530}
              placeholder="Например, что нужно перезвонить"
            />
          </ContentRow>
        </ContentBlock>
      </form>
    </div>
  );
});
